import { SchedulingLeg } from '../../../types/scheduling';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { legBlockedForMissingWeight } from '../../../utils/importWeightRule';
import { legBlockedForMissingHazDocs, SCHEDULING_MISSING_HAZ_DOCS_HELP } from '../../../utils/hazmatPaperworkRule';

interface FinalizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedCount: number;
  legs: SchedulingLeg[];
  isLoading?: boolean;
  /** If true, allows finalization with just prepull date (goes to PENDING_APPT) */
  allowPrepullOnly?: boolean;
}

export default function FinalizeModal({
  isOpen,
  onClose,
  onConfirm,
  selectedCount,
  legs,
  isLoading = false,
  allowPrepullOnly = false,
}: FinalizeModalProps) {
  // Check if any legs are missing required dates
  // - If allowPrepullOnly (LC page): leg needs either prepull OR appt date
  // - Otherwise (Scheduler page): leg needs appt date
  const recoveryLegs = legs.filter(leg => leg.prepull_needed);
  const prepullOnly = recoveryLegs.length > 0 && recoveryLegs.length === legs.length;
  const legsWithoutApptDate = legs.filter(leg => !leg.prepull_needed && (
    allowPrepullOnly ? !leg.proposed_appt_date && !leg.proposed_prepull_date : !leg.proposed_appt_date
  ));

  // Check if any legs have prepull date but no prepull leg (cannot finalize)
  // Skip this check if prepull has already been finalized (leg may have been deleted after finalization)
  const legsWithoutPrepullLeg = legs.filter(
    (leg) => leg.proposed_prepull_date && !leg.has_prepull_leg && !leg.prepull_finalized && !leg.prepull_from_cms
  );

  // Check if any legs have a prepull leg but no prepull date (cannot finalize)
  const legsWithPrepullButNoDate = legs.filter(
    (leg) => (leg.prepull_needed || leg.has_prepull_leg) && !leg.proposed_prepull_date
  );

  // A PRO with a blank Pickup Location (no originating-leg origin) cannot be
  // scheduled — the backend rejects it too (SchedulingService finalize gates)
  const legsWithoutPickupLocation = legs.filter(
    (leg) => !(leg.pickup_name || leg.orig_name || '').trim()
  );

  // An IMPORT PRO with no weight cannot be scheduled — the backend finalize
  // gates (SchedulingService) reject it too. The LC fixes it on the order.
  const legsMissingWeight = legs.filter(legBlockedForMissingWeight);

  // A Haz=Y PRO with no HAZ-MAT PAPERWORK document cannot be scheduled — the
  // backend finalize gates reject it too. The LC uploads the doc on the order.
  const legsMissingHazDocs = legs.filter(legBlockedForMissingHazDocs);

  const legsPendingOverweightApproval = legs.filter(leg => leg.overweight_approval_pending);

  const canFinalize = legsPendingOverweightApproval.length === 0 && legsWithoutApptDate.length === 0 && legsWithoutPrepullLeg.length === 0 && legsWithPrepullButNoDate.length === 0 && legsWithoutPickupLocation.length === 0 && legsMissingWeight.length === 0 && legsMissingHazDocs.length === 0 && !isLoading;

  // Alt+C closes. Finalize gets no chord: it's a one-way transition across every
  // selected leg. The layer still swallows Alt+S so it can't reach the grid.
  useAltHotkeys({ C: isLoading ? null : onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        aria-labelledby="finalize-modal-title"
        onClose={isLoading ? () => {} : onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-scrim bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Panel
                ref={panelRef}
                style={panelStyle}
                className="w-full max-w-md overflow-hidden rounded-xl bg-panel shadow-xl"
              >
                <ModalHeader
                  title={prepullOnly ? "Finalize Pre-pull" : "Finalize Legs"}
                  titleId="finalize-modal-title"
                  onClose={isLoading ? undefined : onClose}
                  dragHandleProps={dragHandleProps}
                />
                <div className="p-6">
                {isLoading ? (
                  // Loading state
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
                    <p className="text-lg font-medium text-heading">
                      Finalizing...
                    </p>
                    <p className="text-sm text-muted mt-2">
                      Please wait while we process {selectedCount} leg{selectedCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                ) : (
                  // Normal state
                  <>
                    <div className="mb-6">
                      <p className="text-gray-600 dark:text-gray-300">
                        {prepullOnly
                          ? `You are finalizing the pre-pull only for ${selectedCount} PRO${selectedCount !== 1 ? 's' : ''}. The confirmed delivery appointment will be preserved.`
                          : `Are you sure you want to finalize ${selectedCount} leg${selectedCount !== 1 ? 's' : ''}? This action cannot be undone.`}
                      </p>

                      {!prepullOnly && recoveryLegs.length > 0 && (
                        <p className="mt-2 text-sm text-muted">
                          For PROs {recoveryLegs.map(leg => leg.pronumber).join(', ')}, you are finalizing the pre-pull only. Their confirmed delivery appointments will be preserved.
                        </p>
                      )}
                      {legsPendingOverweightApproval.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                          <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                            Cannot Finalize: Overweight permit approval required
                          </p>
                          <p className="mt-2 text-xs text-red-700 dark:text-red-300">
                            PROs: {legsPendingOverweightApproval.map(leg => leg.pronumber).join(', ')}
                          </p>
                        </div>
                      )}
                      {legsWithoutApptDate.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                          <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                            Cannot Finalize: {legsWithoutApptDate.length} leg
                            {legsWithoutApptDate.length !== 1 ? 's' : ''} missing {allowPrepullOnly ? 'prepull or appointment date' : 'appointment date'}
                          </p>
                          <ul className="mt-2 text-xs text-red-700 dark:text-red-300 space-y-1">
                            {legsWithoutApptDate.slice(0, 5).map((leg) => (
                              <li key={`${leg.pronumber}-${leg.leg_num}`}>
                                Pro #{leg.pronumber}
                              </li>
                            ))}
                            {legsWithoutApptDate.length > 5 && (
                              <li>...and {legsWithoutApptDate.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                      )}

                      {legsWithoutPrepullLeg.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                          <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                            Cannot Finalize: {legsWithoutPrepullLeg.length} leg
                            {legsWithoutPrepullLeg.length !== 1 ? 's' : ''} missing prepull leg
                          </p>
                          <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                            These legs have a prepull date but no prepull leg exists. Add a prepull leg in CMS first.
                          </p>
                          <ul className="mt-2 text-xs text-red-700 dark:text-red-300 space-y-1">
                            {legsWithoutPrepullLeg.slice(0, 5).map((leg) => (
                              <li key={`${leg.pronumber}-${leg.leg_num}`}>
                                Pro #{leg.pronumber} Leg {leg.leg_num}
                              </li>
                            ))}
                            {legsWithoutPrepullLeg.length > 5 && (
                              <li>...and {legsWithoutPrepullLeg.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                      )}

                      {legsWithoutPickupLocation.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                          <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                            Cannot Finalize: {legsWithoutPickupLocation.length} pro
                            {legsWithoutPickupLocation.length !== 1 ? 's' : ''} missing Pickup Location
                          </p>
                          <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                            These pros have no pickup location set. Set the origin on the originating leg first.
                          </p>
                          <ul className="mt-2 text-xs text-red-700 dark:text-red-300 space-y-1">
                            {legsWithoutPickupLocation.slice(0, 5).map((leg) => (
                              <li key={`${leg.pronumber}-${leg.leg_num}`}>
                                Pro #{leg.pronumber}
                              </li>
                            ))}
                            {legsWithoutPickupLocation.length > 5 && (
                              <li>...and {legsWithoutPickupLocation.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                      )}

                      {legsWithPrepullButNoDate.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                          <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                            Cannot Finalize: {legsWithPrepullButNoDate.length} leg
                            {legsWithPrepullButNoDate.length !== 1 ? 's' : ''} missing prepull date
                          </p>
                          <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                            These legs have a prepull leg but no prepull date has been set.
                          </p>
                          <ul className="mt-2 text-xs text-red-700 dark:text-red-300 space-y-1">
                            {legsWithPrepullButNoDate.slice(0, 5).map((leg) => (
                              <li key={`${leg.pronumber}-${leg.leg_num}`}>
                                Pro #{leg.pronumber}
                              </li>
                            ))}
                            {legsWithPrepullButNoDate.length > 5 && (
                              <li>...and {legsWithPrepullButNoDate.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                      )}

                      {legsMissingWeight.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                          <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                            Cannot Finalize: {legsMissingWeight.length} pro
                            {legsMissingWeight.length !== 1 ? 's' : ''} missing weight
                          </p>
                          <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                            Weight is required on IMPORT orders. Have the LC enter the weight on the order before scheduling.
                          </p>
                          <ul className="mt-2 text-xs text-red-700 dark:text-red-300 space-y-1">
                            {legsMissingWeight.slice(0, 5).map((leg) => (
                              <li key={`${leg.pronumber}-${leg.leg_num}`}>
                                Pro #{leg.pronumber}
                              </li>
                            ))}
                            {legsMissingWeight.length > 5 && (
                              <li>...and {legsMissingWeight.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                      )}

                      {legsMissingHazDocs.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                          <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                            Cannot Finalize: {legsMissingHazDocs.length} pro
                            {legsMissingHazDocs.length !== 1 ? 's' : ''} missing hazmat paperwork
                          </p>
                          <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                            {SCHEDULING_MISSING_HAZ_DOCS_HELP}
                          </p>
                          <ul className="mt-2 text-xs text-red-700 dark:text-red-300 space-y-1">
                            {legsMissingHazDocs.slice(0, 5).map((leg) => (
                              <li key={`${leg.pronumber}-${leg.leg_num}`}>
                                Pro #{leg.pronumber}
                              </li>
                            ))}
                            {legsMissingHazDocs.length > 5 && (
                              <li>...and {legsMissingHazDocs.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                      )}

                      {/* Summary of selected legs */}
                      <div className="mt-4 max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="text-muted sticky top-0 bg-panel">
                            <tr>
                              <th className="text-left py-1">Pro #</th>
                              <th className="text-left py-1">Leg</th>
                              <th className="text-left py-1">Customer</th>
                              <th className="text-left py-1">{recoveryLegs.length ? 'Date to Finalize' : 'Appt Date'}</th>
                            </tr>
                          </thead>
                          <tbody className="text-label">
                            {legs.map((leg) => (
                              <tr key={`${leg.pronumber}-${leg.leg_num}`}>
                                <td className="py-1">{leg.pronumber}</td>
                                <td className="py-1">{leg.leg_num}</td>
                                <td className="py-1 truncate max-w-[100px]">
                                  {leg.customer || '-'}
                                </td>
                                <td className="py-1">
                                  {leg.prepull_needed ? 'Pre-pull: ' : ''}
                                  {(leg.prepull_needed ? leg.proposed_prepull_date : leg.proposed_appt_date)
                                    ? new Date((leg.prepull_needed ? leg.proposed_prepull_date : leg.proposed_appt_date) + 'T00:00:00').toLocaleDateString()
                                    : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3">
                      <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={onConfirm}
                        disabled={!canFinalize}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
                      >
                        <CheckIcon className="w-4 h-4" />
                        Finalize
                      </button>
                    </div>
                  </>
                )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
